import axios from "axios";
import _ from "lodash";
import moment from "moment-timezone";

import { authHeader, getAuthInstance } from "@tailormed/common-client/services/auth";
import { INTERNAL_PROGRAM } from "../constant/assistanceProgram";
import { MANUAL_AUTOMATION_SOURCES } from "../constant/coverages";
import { ALL_TIMES } from "../constant/filters";
import { oktaConfig } from "../constant/okta";
import { PAGE_SIZE, selectedViews } from "../constant/table";
import { DATE_FORMAT, setDateRange } from "../utils/date";
import { buildQueryParamsSearchStrings } from "../utils/search";
import { FILE_TYPES } from "../constant/export";

const SOURCE = "financial_navigation";

const SERVER_URL = `${window.location.origin.replace("4500", "1776")}/`;
const API_VERSION_1 = "api/v1/";
const USERS = "users/";
const PATIENTS = "patients/";
const ApiBaseUrl = `${SERVER_URL}${API_VERSION_1}`;
const CLAIMS = "claims";
const CLAIM_NOTE = "claimNote";
const CLAIM_ATTACHMENTS = "claimattachments";
const PHARMACY_PATIENTS = "pharmacypatients";
const ORDERS = "orders";
const REQUIRED_SIGNER = "requiredsigners";
const REMOTE_SIGNATURE_REQUESTS = "remoteSignatureRequests";
const FILL_NOW = "fillnow";
const ENROLLMENT_NOTE = "enrollmentNote";
const PATIENT_ATTACHMENTS = "patientattachments/";
const SAVED_SIGNATURES = "saved-signature";
const SELF_ENROLL = "self-enroll";
const CONSENT_EVENTS = "consent-events";
const REDI = "redi";

console.log("----------------------------------");
console.log("VERSION:", process.env.REACT_APP_VERSION);
console.log("SERVER_URL:", SERVER_URL);
console.log("----------------------------------");

axios.defaults.withCredentials = true;

const ApiMap = {};
const ApiCallerMap = {};

const CallerType = Object.freeze({
  Generic: 0,
  Auth: 1
});

const ApiNames = Object.freeze({
  Login: "Login",
  LoggedUser: "LoggedUser",
  AssistanceProgramSearch: "AssistanceProgramSearch",
  AssistanceProgram: "AssistanceProgram",
  CheckExpressEnrollConfigurationByApId: "CheckExpressEnrollConfigurationByApId",
  Search: "Search",
  PhiSearch: "PhiSearch",
  Settings: "Settings",
  Users: "Users",
  PhiSearchFreeText: "PhiSearchFreeText",
  ChangeProviderNetwork: "ChangeProviderNetwork",
  SearchFreeText: "SearchFreeText",
  Assignees: "Assignees",
  UserGroups: "UserGroups",
  UsersUnified: "UsersUnified",
  UserGroupMappings: "UserGroupMappings",
  Diagnosis: "Diagnosis",
  PatientDetails: "PatientDetails",
  PatientDetailsNonPhi: "PatientDetailsNonPhi",
  Providers: "Providers",
  getAPSponsors: "getAPSponsors",
  GetRxnormBnPinDrugs: "GetRxnormBnPinDrugs",
  GetUnits: "GetUnits",
  GetVisitTypes: "GetVisitTypes",
  AllPatientsPhiSearch: "AllPatientsPhiSearch",
  AllApplicationsPhiSearch: "AllApplicationsPhiSearch",
  VisitSchedulePhiSearch: "VisitSchedulePhiSearch",
  PendingRenewalPhiSearch: "PendingRenewalPhiSearch",
  HighPrioritiesPhiSearch: "HighPrioritiesPhiSearch",
  AllPatientsSearch: "AllPatientsSearch",
  AllApplicationsSearch: "AllApplicationsSearch",
  AllClaimsPhiSearch: "AllClaimsPhiSearch",
  AllClaimsSearch: "AllClaimsSearch",
  PatientActionsPhiSearch: "PatientActionsPhiSearch",
  PatientActionsSearch: "PatientActionsSearch",
  VisitScheduleSearch: "VisitScheduleSearch",
  PendingRenewalSearch: "PendingRenewalSearch",
  HighPrioritiesSearch: "HighPrioritiesSearch",
  PotentialSavings: "PotentialSavings",
  PotentialSavingsNonPhi: "PotentialSavingsNonPhi",
  CreateDrug: "CreateDrug",
  CountByStatus: "CountByStatus",
  PatientJourney: "PatientJourney",
  PatientJourneyNonPhi: "PatientJourneyNonPhi",
  GenericServices: "GenericServices",
  PatientAutomate: "PatientAutomate",
  ChangeDrugHiddenStatus: "ChangeDrugHiddenStatus",
  UpdateDiscontinuedDate: "UpdateDiscontinuedDate",
  UpdateOOPEstimation: "UpdateOOPEstimation",
  GetOOPEstimations: "GetOOPEstimations",
  PotentialSavingsJourney: "PotentialSavingsJourney",
  PotentialSavingsJourneyNonPhi: "PotentialSavingsJourneyNonPhi",
  ApproveInsuranceOpportunity: "ApproveInsuranceOpportunity",
  AddAssistanceProgram: "AddAssistanceProgram",
  AssociateDrug: "AssociateDrug",
  PatientCoverages: "PatientCoverages",
  PatientUnknownAndInactiveInsuranceInfo: "PatientUnknownAndInactiveInsuranceInfo",
  Policies: "Policies",
  ApplicationAttachments: "ApplicationAttachments",
  PotentialSaving: "PotentialSaving",
  DocusignCreate: "DocusignCreate",
  ApplicationEdit: "ApplicationEdit",
  ApplicationEditHistorical: "ApplicationEditHistorical",
  ApplicationEditNonPhi: "ApplicationEditNonPhi",
  ApplicationDelete: "ApplicationDelete",
  PatientReapply: "PatientReapply",
  PatientReapplyNonPhi: "PatientReapplyNonPhi",
  RelevanceForRenewal: "RelevanceForRenewal",
  shippingOptions: "shippingOptions",
  searchSavedList: "searchSavedList",
  CreateFilterList: "CreateFilterList",
  UpdateFilterList: "UpdateFilterList",
  FindSavedListById: "FindSavedListById",
  DeleteSavedList: "DeleteSavedList",
  getAllClaims: "getAllClaims",
  CreateClaim: "CreateClaim",
  UpdateClaim: "UpdateClaim",
  CreateAttachmentClaim: "CreateAttachmentClaim",
  DeleteAttachmentClaim: "DeleteAttachmentClaim",
  ToggleClaimActiveStatus: "ToggleClaimActiveStatus",
  DuplicateClaimAttachments: "DuplicateClaimAttachments",
  FetchPapPDFMetaData: "FetchPapPDFMetaData",
  DeleteEnrollmentNote: "DeleteEnrollmentNote",
  AddEnrollmentNote: "AddEnrollmentNote",
  GetAllEnrollmentNotes: "GetAllEnrollmentNotes",
  getRecentPatients: "getRecentPatients",
  UpdateEnrollmentNote: "UpdateEnrollmentNote",
  GetPatientBnPinDrugs: "GetPatientBnPinDrugs",
  GetSelfEnrollInvitations: "GetSelfEnrollInvitations",
  GetInvitationsByPatientId: "GetInvitationsByPatientId",
  FrontendLog: "FrontendLog",
  PaytientSendToPatient: "PaytientSendToPatient",
  PaytientAnalytics: "PaytientAnalytics",
  PaytientWebLinkClick: "PaytientWebLinkClick",
  GetPatientJourney: "GetPatientJourney",
  // Physician Management
  GetPhysicians: "GetPhysicians",
  CreatePhysician: "CreatePhysician",
  UpdatePhysician: "UpdatePhysician",
  CheckPhysicianDuplicate: "CheckPhysicianDuplicate",
  GetPatientNotes: "GetPatientNotes",
  GetPatientAttachmentsCount: "GetPatientAttachmentsCount"
});

function getAccessToken() {
  const oktaAuth = getAuthInstance(oktaConfig.redirectUri);
  return oktaAuth.getAccessToken();
}

async function axiosWithAuthentication(config) {
  const token = getAccessToken();
  config.headers = authHeader(token);
  return axios(config);
}

const add = (name, action, caller = CallerType.Generic) => {
  Object.assign(ApiCallerMap, { [name]: caller });
  Object.assign(ApiMap, { [name]: (...args) => action(...args) });
};

const addAuth = (name, action) => add(name, action, CallerType.Auth);

const orderMap = {
  ascend: "ASC",
  descend: "DESC"
};

const formateSorting = (sorter) => ({
  ...(sorter && {
    sorting: {
      orderBy: sorter.columnKey,
      sort: orderMap[sorter?.order]
    }
  })
});

const parseSearchFormParams = ({ selectedView, page, limit, filters = {}, sorter, searchBy }) => {
  const skipValue = (page - 1) * PAGE_SIZE;
  const {
    multiples,
    dates,
    starredPatients,
    inActiveTreatment,
    hideDeceasedPatients,
    enrollmentsCreatedByPatients,
    expressEnrollEligible,
    isTmConnectEligible,
    claimExists,
    groupPatientVisits,
    insuranceCombination,
    isUnassigned,
    isNoFacility,
    isNoClaimFacility,
    isUnassignedEnrollment,
    isUnassignedClaim,
    hasInsuranceChanges,
    insuranceChangeType,
    insuranceChangeTimeframe
  } = filters;

  const selectedViewField = selectedView === "high-priority" ? "top-opportunity" : selectedView;

  const numberFilters = {};
  const datesFilters = {};
  const multiplesFilters = {};
  let internalProgram = false;

  if (Object.keys(filters).length) {
    dates &&
      Object.entries(dates).forEach(([dateFilter, { fromDate, toDate, value }]) => {
        // Note: MVP-14581 Parse Dates based on the saved fromDate, toDate, and value. Value is one of the defined date range criteria we support.
        if (fromDate && toDate && !value) {
          datesFilters[dateFilter] = [
            moment(fromDate, DATE_FORMAT).startOf("day").format("YYYY-MM-DD HH:mm"),
            moment(toDate, DATE_FORMAT).endOf("day").format("YYYY-MM-DD HH:mm")
          ];
        } else if (selectedViewField !== selectedViews.visitSchedule && dateFilter === "visitScheduleDateRange") {
          datesFilters[dateFilter] = setDateRange(ALL_TIMES);
        } else datesFilters[dateFilter] = setDateRange(value || fromDate);
      });

    multiples &&
      Object.entries(multiples).forEach(([multipleFilter, selectedValues]) => {
        multiplesFilters[multipleFilter] = [];

        if (
          multipleFilter == "rxnormDrugs" ||
          multipleFilter == "claimRxnormDrugs" ||
          multipleFilter == "potentialSavingsRxnormDrugs"
        ) {
          // Filter out null/undefined values from drug arrays (defensive programming)
          Object.values(selectedValues).forEach((drug) => {
            if (drug != null) {
              const { tty, ttyCui, bnName, pinName, pinCui, tmDisplayName, id } = drug;
              multiplesFilters[multipleFilter].push({ tty, ttyCui, bnName, pinName, pinCui, tmDisplayName, id });
            }
          });
        } else {
          Object.entries(selectedValues).forEach(([, { value }]) => {
            multiplesFilters[multipleFilter].push(value);
          });
        }
      });

    if (multiplesFilters.potentialSavingsTypes?.length) {
      if (multiplesFilters.potentialSavingsTypes.includes(INTERNAL_PROGRAM)) {
        // Note: if user selected the internal option, then pass the flag with the payload
        internalProgram = true;
      }
    }

    if (filters?.numbers && Object.keys(filters.numbers).length) {
      Object.entries(filters.numbers).forEach(([key, value]) => value && (numberFilters[key] = value));
    }
  }

  const parsedFilters = {
    ...numberFilters,
    ...datesFilters,
    ...multiplesFilters,
    selectedView: selectedViewField,
    starredPatients,
    inActiveTreatment,
    hideDeceasedPatients,
    enrollmentsCreatedByPatients,
    expressEnrollEligible,
    isTmConnectEligible,
    claimExists,
    groupPatientVisits,
    insuranceCombination,
    isUnassigned,
    isNoFacility,
    isNoClaimFacility,
    isUnassignedEnrollment,
    isUnassignedClaim,
    internalProgram,
    hasInsuranceChanges,
    insuranceChangeType,
    insuranceChangeTimeframe
  };
  // NOTE: We remove all the filters that are empty or not defined
  const usedParsedFilters = _.pickBy(parsedFilters, (value) => {
    if (_.isArray(value)) {
      return value.length;
    } else if (_.isObject(value)) {
      return !_.isEmpty(value);
    } else {
      return !_.isNil(value);
    }
  });

  const searchData = {
    page,
    skip: skipValue,
    limit: PAGE_SIZE || limit,
    filters: usedParsedFilters,
    ...formateSorting(sorter),
    ...(searchBy && { searchBy }),
    ...(selectedView && { selectedView: selectedViewField })
  };

  return searchData;
};

const buildSearchRequest = ({ selectedView, page, limit, filters, sorter, isPhi }) => {
  const MAX_HTTP_URL_SIZE = 1500; // NOTE: We take a 500 chars buffer for the rest of the URL
  const parsedFilters = parseSearchFormParams({ selectedView, page, limit, filters, sorter });
  const queryParamsFilters = buildQueryParamsSearchStrings(parsedFilters);

  let request = {};
  const url = `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/${isPhi ? "phi-" : ""}search`;

  if (queryParamsFilters.length < MAX_HTTP_URL_SIZE) {
    const urlWithQueryParams = `${url}?${queryParamsFilters}`;

    request = {
      method: "GET",
      url: urlWithQueryParams
    };
  } else {
    request = {
      method: "POST",
      url,
      data: parsedFilters
    };
  }

  return request;
};

/* Note: This will parse the server filter values into the actual Filter Inputs/controls values. */
const serverFiltersDecoder = (filters, serverFormattedFilters) => {
  const decodedFilters = { ...filters };
  Object.keys(decodedFilters).forEach((key) => {
    if (key in serverFormattedFilters.dates) {
      decodedFilters[key] = serverFormattedFilters?.dates[key]?.value;
    } else if (key === "drugs") {
      decodedFilters[key] = serverFormattedFilters?.multiples?.drugsNames;
    }
  });
  return decodedFilters;
};

const viewPatientAttachmentURL = (attachmentId, attachmentUniqueId) =>
  `${ApiBaseUrl}${PATIENT_ATTACHMENTS}view/${attachmentId}/${attachmentUniqueId}`;

add(ApiNames.Login, (username, password) => {
  const data = new FormData();
  data.append("username", username);
  data.append("password", password);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}token/frontend`,
    data
  };
});

addAuth(ApiNames.LoggedUser, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}logged-user/frontend`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Users, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}frontend`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Assignees, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}assignable`,
  headers: authHeader(token)
}));

addAuth(ApiNames.UserGroups, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}userGroups`,
  headers: authHeader(token)
}));

addAuth(ApiNames.UsersUnified, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}${USERS}unified`,
  headers: authHeader(token)
}));

addAuth(ApiNames.UserGroupMappings, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}userGroups/mappings`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Settings, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}settings`,
  headers: authHeader(token)
}));

addAuth(ApiNames.PhiSearch, (token, selectedView, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView, page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.AllPatientsPhiSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "all-patients", page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.AllApplicationsPhiSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "all-applications", page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.AllClaimsPhiSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "all-claims", page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.VisitSchedulePhiSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "visit-schedule", page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PendingRenewalPhiSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "pending-renewal", page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.HighPrioritiesPhiSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "high-priority", page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.AllPatientsSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "all-patients", page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.AllApplicationsSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "all-applications", page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.VisitScheduleSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "visit-schedule", page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PendingRenewalSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "pending-renewal", page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.HighPrioritiesSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "high-priority", page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.AllClaimsSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "all-claims", page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientActionsPhiSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "patient-actions", page, limit, filters, sorter, isPhi: true });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientActionsSearch, (token, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView: "patient-actions", page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.Search, (token, selectedView, page = 1, limit = 25, filters = {}, sorter = {}) => {
  const request = buildSearchRequest({ selectedView, page, limit, filters, sorter });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PhiSearchFreeText, (token, page, text, searchBy, sorter = {}) => {
  // NOTE: When searching by free text we always assume that there's PHI in the request, so we use the POST method
  const limit = PAGE_SIZE;
  const parsedSearchParams = parseSearchFormParams({ selectedView: "search-results", page, limit, sorter, searchBy });
  const urlEncodedData = buildQueryParamsSearchStrings(parsedSearchParams);
  const data = {
    query: text
  };
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/phi-search?${urlEncodedData}`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.ChangeProviderNetwork, (token, policy_id, patient_id, journey_id, network) => {
  const data = new FormData();
  data.append("journey_id", journey_id);
  data.append("patient_id", patient_id);
  data.append("network", network);
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}policy/${policy_id}/changeprovidernetwork`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.SearchFreeText, (token, page = 1, text, searchBy, sorter = {}) => {
  // NOTE: When searching by free text we always assume that there's PHI in the request, so we use the POST method
  const limit = PAGE_SIZE;
  const parsedSearchParams = parseSearchFormParams({ selectedView: "search-results", page, limit, sorter, searchBy });
  const urlEncodedData = buildQueryParamsSearchStrings(parsedSearchParams);
  const data = {
    query: text
  };
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/phi-search?${urlEncodedData}`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.AssistanceProgram, (token, id) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}assistancePrograms/${id}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.CheckExpressEnrollConfigurationByApId, (token, id) => {
  return {
    method: "HEAD",
    url: `${SERVER_URL}${API_VERSION_1}assistanceprogram/${id}/expressenrollconfigurations`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientDetails, (token, patientId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPatientPhis/${patientId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientDetailsNonPhi, (token, patientId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPatients/${patientId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.GetUnits, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}setting/getAllUnits`,
  headers: authHeader(token)
}));

addAuth(ApiNames.GetVisitTypes, (token) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}patients/visitTypes`,
  headers: authHeader(token)
}));

addAuth(ApiNames.Providers, (token, providerType) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}providers`,
  params: { type: providerType },
  headers: authHeader(token)
}));

// Physician Management APIs
addAuth(ApiNames.GetPhysicians, (token, params = {}) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}providers/physicians`,
  params,
  headers: authHeader(token)
}));

addAuth(ApiNames.CreatePhysician, (token, physicianData) => {
  // Map stateLicense to stateLisence (database field name has typo)
  const { stateLicense, ...rest } = physicianData;
  const mappedData = stateLicense !== undefined ? { ...rest, stateLisence: stateLicense } : rest;
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}providers`,
    data: { provider: { type: "doctor", ...mappedData } },
    headers: authHeader(token)
  };
});

addAuth(ApiNames.UpdatePhysician, (token, physicianId, physicianData) => {
  // Map stateLicense to stateLisence (database field name has typo)
  const { stateLicense, ...rest } = physicianData;
  const mappedData = stateLicense !== undefined ? { ...rest, stateLisence: stateLicense } : rest;
  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}providers/${physicianId}`,
    data: { provider: { type: "doctor", ...mappedData } },
    headers: authHeader(token)
  };
});

addAuth(
  ApiNames.CheckPhysicianDuplicate,
  (token, npi, address, suiteNumber, facility, city, state, zipCode, excludeId) => ({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}providers/physicians/check-duplicate`,
    data: { npi, address, suiteNumber, facility, city, state, zipCode, excludeId },
    headers: authHeader(token)
  })
);

addAuth(ApiNames.getAPSponsors, (token) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}assistanceprograms/getAPSponsors`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PotentialSavings, (token, queryIds) => {
  const data = queryIds.map((id) => ["ids[]", id]);

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavingPhis?${urlEncodedData}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PotentialSavingsNonPhi, (token, queryIds) => {
  const data = queryIds.map((id) => ["ids[]", id]);

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavings?${urlEncodedData}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PotentialSavingsJourney, (token, journeyId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavingPhis?journey=${encodeURIComponent(journeyId)}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PotentialSavingsJourneyNonPhi, (token, journeyId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavings?journey=${encodeURIComponent(journeyId)}`,
    headers: authHeader(token)
  };
});

const addDrugAPI = async ({ patientId, rxnormDrug, medicationData, uncoveredDrug, hiddenDrug }) => {
  const data = {
    patientId,
    medicationData,
    rxnormDrug,
    uncoveredDrug,
    hiddenDrug
  };

  return await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/drug`,
    data
  });
};

//[DRUGS-TODO-AMIT] - check delete of non iv
const deleteMedicationAPI = async ({ phiMedicationId, patientId }) => {
  return axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/drug`,
    data: { phiMedicationId }
  });
};

const editMedicationAPI = async ({ patientId, phiMedicationId, rxnormDrug, medicationData }) => {
  return await axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/drug`,
    data: { phiMedicationId, rxnormDrug, medicationData }
  });
};

addAuth(ApiNames.CountByStatus, (token) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/countbystatus/`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientJourney, (token, journeyId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPatientJourneyPhis/${journeyId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientJourneyNonPhi, (token, journeyId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPatientJourneys/${journeyId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.GetPatientJourney, (token, patientId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patient/getPatientJourney/${patientId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.GenericServices, (token, queryIds) => {
  const data = queryIds.map((id) => ["ids[]", id]);

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}genericServices?${urlEncodedData}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientAutomate, (token, patientId, automateRule) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/automate/${patientId}`,
    headers: authHeader(token),
    data: { automateRule }
  };
});

addAuth(ApiNames.ChangeDrugHiddenStatus, (token, patientId, drugId, hide) => {
  const data = new FormData();
  data.append("patientId", patientId);
  data.append("drugId", drugId);
  data.append("hide", hide);

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/change-drug-hidden-status`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.UpdateDiscontinuedDate, (token, patientId, journeyEvent, discontinuedDate) => {
  const data = new FormData();
  data.append("patient", patientId);
  data.append("journeyEvent", journeyEvent);
  data.append("discontinuedDate", moment(discontinuedDate));

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/update-discontinued-date`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.UpdateOOPEstimation, (token, data) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}journey/updateOOPEstimation`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.GetOOPEstimations, (token, data) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}journey/getOOPEstimations`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.PatientCoverages, (token, patientId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}coverages?patient=${patientId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientUnknownAndInactiveInsuranceInfo, (token, patientId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}coverage/${patientId}/getUnknownAndInactiveInsuranceInfoForPatient`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.Policies, (token, queryIds) => {
  const data = queryIds.map((id) => ["ids[]", id]);

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}policies?${urlEncodedData}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.ApplicationEdit, (token, papId, papData, applicationIdToUpdate, isInternal = false) => {
  const data = {
    "financial-navigation-potential-saving-phi": { ...papData, applicationIdToUpdate }
  };

  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavingPhis/${papId}?isInternal=${
      isInternal === true ? "1" : "0"
    }`,
    headers: authHeader(token),
    data
  };
});

addAuth(
  ApiNames.ApplicationEditNonPhi,
  (token, papId, papData, applicationIdToUpdate, isInternal = false, associatedDrugs) => {
    const data = {
      "financial-navigation-potential-saving": { ...papData, applicationIdToUpdate, associatedDrugs }
    };

    return {
      method: "PUT",
      url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavings/${papId}?isInternal=${
        isInternal === true ? "1" : "0"
      }`,
      headers: authHeader(token),
      data
    };
  }
);

addAuth(ApiNames.ApplicationEditHistorical, (token, patientId, papId, updatedApplications) => {
  const data = {
    applicationsToUpdate: updatedApplications.map((app) => ({
      ...app
    })),
    patientId
  };

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}paps/${papId}/updateApplications/`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.ApplicationDelete, (token, patientId, papId, applicationId) => {
  const data = {
    removedApplicationIDs: [applicationId],
    patientId
  };

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}paps/${papId}/updateApplications/`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.PotentialSaving, (token, papId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavingPhis/${papId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PatientReapply, (token, papId, status, createNewApplication, associatedDrugs, rxnormDrugIds) => {
  const data = {
    status,
    createNewApplication,
    associatedDrugs,
    rxnormDrugIds
  };
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavingPhis/${papId}/reapply`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.PatientReapplyNonPhi, (token, papId, status, createNewApplication, associatedDrugs, rxnormDrugIds) => {
  const data = {
    status,
    createNewApplication,
    associatedDrugs,
    rxnormDrugIds
  };
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavings/${papId}/reapply`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.RelevanceForRenewal, (token, papId, applicationId, isIrrelevant) => {
  const data = new FormData();
  data.append("isIrrelevant", isIrrelevant);
  data.append("applicationId", applicationId);

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSaving/${papId}/setIrrelevantForRenewal`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.shippingOptions, (token, patientId, papId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}autofillForms/${patientId}/get-shipping-options/${papId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.searchSavedList, (token, data) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/search-filter-list`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.CreateFilterList, (token, formData) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/new-filter-list`,
    headers: authHeader(token),
    data: formData.data
  };
});

addAuth(ApiNames.CreateClaim, (token, formData) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIMS}`,
    headers: authHeader(token),
    data: formData
  };
});

addAuth(ApiNames.CreateAttachmentClaim, (token, attachmentClaimdata) => {
  const { patient, file, claim } = attachmentClaimdata;
  const data = new FormData();

  data.append("patient", patient);
  data.append("claim", claim);
  data.append("file", file);

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIM_ATTACHMENTS}/upload`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.DuplicateClaimAttachments, (token, duplicationPayload) => {
  const { patientId, targetClaimId, sourceClaimId, skippedAttachments = [] } = duplicationPayload;

  const data = {
    patientId,
    sourceClaimId,
    targetClaimId,
    skippedAttachments
  };

  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIM_ATTACHMENTS}/duplicate`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.FetchPapPDFMetaData, (token, papId, applicationId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}autofillForms/metadata?papId=${papId}&applicationId=${applicationId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.DeleteAttachmentClaim, (token, attachmentClaimId) => {
  return {
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIM_ATTACHMENTS}/` + attachmentClaimId,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.UpdateClaim, (token, data, claimId) => {
  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIMS}/${claimId}`,
    headers: authHeader(token),
    data: data
  };
});

addAuth(ApiNames.ToggleClaimActiveStatus, (token, patientId, claimId, isActive) => {
  const data = {
    patientId,
    claim: {
      isActive
    }
  };

  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIMS}/${claimId}`,
    headers: authHeader(token),
    data: data
  };
});

addAuth(ApiNames.UpdateFilterList, (token, data, filterListId) => {
  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/update/${filterListId}`,
    headers: authHeader(token),
    data: data
  };
});

addAuth(ApiNames.FindSavedListById, (token, filterListId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/${filterListId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.ApplicationAttachments, (token, applicationId, filterBySource = null) => ({
  method: "POST",
  headers: authHeader(token),
  url: `${SERVER_URL}${API_VERSION_1}applicationattachments/list`,
  data: {
    application: applicationId,
    ...(filterBySource && { filterBySource })
  }
}));

addAuth(ApiNames.getAllClaims, (token, applicationId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}applications/${applicationId}/${CLAIMS}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.GetAllEnrollmentNotes, (token, assistanceProgramApplicationId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${ENROLLMENT_NOTE}?assistanceProgramApplicationId=${assistanceProgramApplicationId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.AddEnrollmentNote, (token, assistanceProgramApplication, note) => ({
  method: "POST",
  headers: authHeader(token),
  url: `${SERVER_URL}${API_VERSION_1}${ENROLLMENT_NOTE}`,
  data: {
    assistanceProgramApplication,
    note
  }
}));

addAuth(ApiNames.DeleteEnrollmentNote, (token, noteId) => {
  return {
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}${ENROLLMENT_NOTE}/${noteId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.GetPatientNotes, (token, patientId) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes`,
    headers: authHeader(token),
    data: { patient_id: patientId }
  };
});

addAuth(ApiNames.GetPatientAttachmentsCount, (token, patientId) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}count`,
    headers: authHeader(token),
    data: {
      patient: patientId,
      options: { excludeCylcesSpecific: true }
    }
  };
});

addAuth(ApiNames.GetSelfEnrollInvitations, (token, filters = {}, pagination = {}) => {
  const { page = 1, limit = PAGE_SIZE } = pagination;
  const request = buildSearchRequest({
    selectedView: "patient-actions",
    page,
    limit,
    filters,
    sorter: {},
    isPhi: true
  });
  return {
    ...request,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.GetInvitationsByPatientId, (token, patientId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}self-enroll/invitation/patient/${patientId}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.getRecentPatients, (token, page = 1) => {
  const limit = PAGE_SIZE;
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/getRecentFinancialNavigationPatients?product=financial-navigation&page=${page}&limit=${limit}`,
    headers: authHeader(token)
  };
});

const getApplicationUsedAmountsAPI = (applicationId) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}applicationusedamount/${applicationId}`
  });

const updateApplicationUsedAmountsAPI = (rowsToUpdate, applicationId) => {
  const data = {
    rowsToUpdate,
    applicationId
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationusedamount`,
    data
  });
};

const getLoginImagesAPI = () =>
  axios({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}app/login-images/frontend`
  });
const initializePendoAnonymous = () =>
  axios({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}pendo/initializePendo`,
    data: {
      user: null,
      application: process.env.REACT_APP_ANALYTICS_APPLICATION,
      isAnonymous: true
    }
  });

const searchDiagnosisIcdsAPI = (text) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}custom-dropdown/?searchType=diagnosisCode&query=${text}`
  });
const searchRxnormDrugsAPI = ({ text = "", bnCui = "", pinCui = "" }) => {
  const queryObject = _.omitBy(
    {
      query: text,
      bnCui,
      pinCui
    },
    (param) => !param
  );

  const params = new URLSearchParams(queryObject);

  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}rxnorm-drug/search?${params}`
  });
};

const getPatientBnPinDrugsAPI = (patientId) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/bn-pin-drugs`
  });

const searchDiagnosisNamesAPI = (text) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}custom-dropdown/?searchType=diagnosisName&query=${text}`
  });

const getValidForEbvPayersAPI = () => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}payers/valid-for-ebv-payers`
  });
};

const getClaimNotesAPI = (patientId, claimId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIM_NOTE}?patientId=${patientId}&claimId=${claimId}`
  });
};

const checkOtherPayers = (otherPayersNames) => {
  const formData = new FormData();
  otherPayersNames.map((otherPayerName) => formData.append("query[]", otherPayerName));
  formData.append("includeSelfPayOption", false);

  const data = [...formData.entries()];

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}payers/search-auto-complete`,
    data: urlEncodedData
  });
};

const searchIcdToDiagnoses = (text) => {
  const data = new FormData();
  data.append("query", text);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}icdToDiagnoses/search`,
    data
  });
};

const starPatient = (patientId, starred) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patient/starPatient`,
    data: `patientId=${patientId}&starred=${starred}`
  });
};

const updatePatientStatusAPI = (patientId, status) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  data.append("status", status);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/status/`,
    data
  });
};

const updateFollowupDateAPI = (patientId, followupDate) => {
  const data = new FormData();
  data.append("followupDate", followupDate);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/updateFollowUpDate`,
    data
  });
};

const updatePreferredLanguageAPI = (patientId, preferredLanguage) => {
  const data = { preferredLanguage: preferredLanguage ? preferredLanguage : null };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/updatePreferredLanguage`,
    data
  });
};

const updatePatientAssigneesAPI = (patientId, assignees) => {
  const data = {
    patient_id: patientId,
    assignee: assignees
  };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/assignee`,
    data
  });
};

const updatePatientPhysician = (patientId, physicianId) => {
  const data = new FormData();

  data.append("patient_id", patientId);
  if (physicianId) {
    data.append("doctor", physicianId);
    return axiosWithAuthentication({
      method: "POST",
      url: `${SERVER_URL}${API_VERSION_1}patients/doctor`,
      data
    });
  } else {
    return axiosWithAuthentication({
      method: "POST",
      url: `${SERVER_URL}${API_VERSION_1}patients/clearPhysician`,
      data
    });
  }
};

const updatePatientFacility = (patientId, facilityIds) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/facility`,
    data: {
      patient_id: patientId,
      facilities: Array.isArray(facilityIds) ? facilityIds : []
    }
  });
};

const requestManualAnalysis = (patientId) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/request-analysis`,
    data
  });
};

const getPatientData = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/patient-data`
  });
};

const getPatientNotesAPI = (patientId) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes`,
    data
  });
};

const addPatientNoteAPI = (patientId, noteText) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  data.append("note", noteText);
  data.append("creationDate", new Date().toISOString());
  data.append("updatedAt", "null");
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes/add`,
    data
  });
};

const deletePatientNoteAPI = (patientId, noteId) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  data.append("note_id", noteId);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes/delete`,
    data
  });
};

const editPatientNoteAPI = (patientId, noteId, noteText) => {
  const data = new FormData();
  data.append("patient_id", patientId);
  data.append("note_id", noteId);
  data.append("note", noteText);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}notes/edit`,
    data
  });
};

const createPatientsTaskAPI = (taskData) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}todos`,
    data: { todo: taskData }
  });
};

const getPatientsTasksAPI = (
  filterData,
  addPatientData,
  patientId = null,
  pageSize,
  page,
  usePagination = true,
  isExcel = false
) => {
  const limit = page * pageSize;
  const data = { addPatientData, ...(patientId && { patientId }), limit, usePagination, isExcel };
  data.filters = filterData;
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}todos/list`,
    data
  });
};

const exportTasksReportAPI = (filterData) => {
  const data = { addPatientData: true, isExcel: true };
  data.filters = filterData;
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}todos/list`,
    data,
    responseType: "blob"
  });
};

const exportListAPI = ({
  listType,
  selectedView,
  filters = {},
  sorting = {},
  visibleColumns = [],
  savedListId = null,
  fileType = FILE_TYPES.CSV
}) => {
  const formattedSorting = sorting?.columnKey
    ? {
        orderBy: sorting.columnKey,
        sort: orderMap[sorting.order]
      }
    : {};

  const data = {
    listType,
    selectedView,
    filters,
    sorting: formattedSorting,
    visibleColumns,
    savedListId,
    fileType,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}export`,
    data
  });
};

const editPatientsTaskAPI = (todo) => {
  return axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}todos/${todo.id}`,
    data: { todo }
  });
};

const deletePatientsTaskAPI = (taskData) => {
  const data = {};
  data.todo = { ...taskData, status: "deleted" };

  return axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}todos/${taskData.id}`,
    data
  });
};

const listApplicationAttachmentApi = (applicationId, filterBySource = null) =>
  axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationattachments/list`,
    data: {
      application: applicationId,
      ...(filterBySource && { filterBySource })
    }
  });

const countApplicationAttachmentApi = (applicationId, filterBySource = null) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationattachments/count`,
    data: {
      application: applicationId,
      ...(filterBySource && { filterBySource })
    }
  });
};

const uploadApplicationAttachmentApi = (applicationId, patientId, file, source = null) => {
  const data = new FormData();
  data.append("application", applicationId);
  data.append("patient", patientId);
  if (source) {
    data.append("source", source);
  }
  // File must be appended to the form data last. Otherwise Skipper adds arguments
  // appended after to fileSize, which breaks our uploading logic
  data.append("file", file);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationattachments/upload`,
    data
  });
};

const deleteApplicationAttachmentApi = (attachmentId, attachmentUniqueId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}applicationattachments/delete/${attachmentId}/${attachmentUniqueId}`
  });
};

const viewApplicationAttachmentApi = (attachmentId, attachmentUniqueId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}applicationattachments/view/${attachmentId}/${attachmentUniqueId}`
  });
};

const downloadApplicationAttachmentApi = async (attachmentId, attachmentUniqueId) => {
  const token = getAccessToken();
  const resp = await fetch(
    `${SERVER_URL}${API_VERSION_1}applicationattachments/download/${attachmentId}/${attachmentUniqueId}`,
    {
      headers: authHeader(token),
      method: "GET"
    }
  );
  return resp.blob();
};

const downloadApplicationFormAttachmentApi = async (applicationFormAttachmentId) => {
  const token = getAccessToken();
  const resp = await fetch(`${SERVER_URL}${API_VERSION_1}applicationformattachments/${applicationFormAttachmentId}`, {
    headers: authHeader(token),
    method: "GET"
  });

  if (!resp.ok) {
    throw new Error("Network response was not ok");
  }

  return await resp.blob();
};

const uploadPatientAttachmentAPI = (file, patientId) => {
  const data = new FormData();
  data.append("patient", patientId);
  data.append("file", file);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}upload`,
    data
  });
};

const cancelDocusignSignatureRequestAPI = async (applicationFormId) => {
  return axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}applicationform/${applicationFormId}/docusignSignatureRequest`
  });
};

const uploadAttachmentAPI = (file) => {
  const data = new FormData();
  data.append("file", file);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}upload`,
    data
  });
};

const getClaimAttachmentAPI = (claimId) => {
  const data = new FormData();
  data.append("claim", claimId);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${CLAIM_ATTACHMENTS}/list`,
    data
  });
};

const getPatientAttachmentsAPI = (patientId, filterData) => {
  const data = {
    patient: patientId,
    options: { ...filterData, excludeCylcesSpecific: true }
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}list`,
    data
  });
};

const getPatientAttachmentsCountAPI = (patientId) => {
  const data = {
    patient: patientId,
    options: { excludeCylcesSpecific: true }
  };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}count`,
    data
  });
};

const searchPatientPayers = (query) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}payers/search-for-patient?query=${query}`
  });
};

const getPharmacyPatientsOrdersAPI = (patientId, pap) => {
  const data = {
    patient: patientId,
    pap
  };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PHARMACY_PATIENTS}/${ORDERS}`,
    data
  });
};

const deletePatientAttachmentAPI = (attachmentId, attachmentUniqueId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}delete/${attachmentId}/${attachmentUniqueId}`
  });
};

const downloadPatientAttachmentAPI = async (attachmentId, attachmentUniqueId) => {
  const token = getAccessToken();

  return await new Promise(function (resolve, reject) {
    fetch(`${SERVER_URL}${API_VERSION_1}${PATIENT_ATTACHMENTS}download/${attachmentId}/${attachmentUniqueId}`, {
      headers: authHeader(token),
      method: "GET"
    })
      .then((resp) => {
        return resp.blob();
      })
      .then(resolve)
      .catch(reject);
  });
};

const downloadClaimAttachmentAPI = async (attachmentId) => {
  const token = getAccessToken();

  return await new Promise(function (resolve, reject) {
    fetch(`${SERVER_URL}${API_VERSION_1}${CLAIM_ATTACHMENTS}/${attachmentId}/download`, {
      headers: authHeader(token),
      method: "GET"
    })
      .then((resp) => {
        return resp.blob();
      })
      .then(resolve)
      .catch(reject);
  });
};

const addNewPatient = (patientData) => {
  const formData = new FormData();

  Object.entries({ ...patientData, source: SOURCE }).map(([title, value]) => {
    formData.append(title, value);
  });

  const data = [...formData.entries()];

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/newpatient`,
    data: urlEncodedData
  });
};

const validateMrnUniqueness = async (mrn) =>
  axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/validateMrnUniqueness`,
    data: { mrn }
  });

const getNotificationsAPI = () =>
  axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}programNotifications/list`
  });

const getSelfEnrollInvitationsAPI = (filters = {}, pagination = {}) => {
  const { page = 1, limit = PAGE_SIZE } = pagination;
  const request = buildSearchRequest({
    selectedView: "patient-actions",
    page,
    limit,
    filters,
    sorter: {},
    isPhi: true
  });

  return axiosWithAuthentication(request);
};

const getInvitationsByPatientIdAPI = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}self-enroll/invitation/patient/${patientId}`
  });
};

const markNotificationAPI = (notificationID, val) => {
  const data = new FormData();
  data.append("notificationID", notificationID);
  data.append("newState", val);
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}programNotifications/markNotification`,
    data
  });
};

const getImportedStatusesAPI = () =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patient-data/get-imported-statuses`
  });

const getTopOpportunityPatientsAPI = (filters, sorter = {}, page = 1, limit = 25) => {
  const request = buildSearchRequest({
    selectedView: "top-opportunity",
    page,
    limit,
    filters,
    sorter,
    isPhi: true
  });
  return axiosWithAuthentication(request);
};

const getNumberOfPatientsAPI = (filters, skip = 0, limit = 25, selectedView) => {
  const data = { filters, page: 1, skip, limit, selectedView };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/phi-search`,
    data
  });
};

const getManagePatientDetails = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/edit/details`
  });
};

const setPatientDetailsAPI = (patientId, data, skipAutomations) => {
  //TODO: remove this, ember patch
  if (data.financial) {
    if (data.financial.filesTaxes === true) {
      data.financial.filesTaxes = "true";
    } else if (data.financial.filesTaxes === false) {
      data.financial.filesTaxes = "false";
    }
  }

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patient/edit/details`,
    data: {
      patientId,
      data,
      skipAutomations
    }
  });
};

const updatePatientMedicationsAPI = ({
  patientId,
  manualDrugEvents,
  importedDrugEvents,
  patientHiddenDrugs,
  physicianDrugs,
  uncoveredDrugs,
  skipAutomations
}) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/update-patient-medications-info`,
    data: {
      manualDrugEvents,
      importedDrugEvents,
      patientHiddenDrugs,
      physicianDrugs,
      uncoveredDrugs,
      skipAutomations
    }
  });
};

const updateInsuranceAPI = (patientId, previousInsuranceRows, newInsuranceRows) =>
  axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/insuranceRows`,
    data: {
      previousInsuranceRows,
      newInsuranceRows
    }
  });

const updateUninsuredStatusAPI = (patientId, isUninsured) =>
  axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/insuranceRows/changeInsuredStatus`,
    data: { isUninsured }
  });

const createPatientInsurancesReviewAPI = (patientId, reviewedDate) =>
  axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/createNewInsuranceReview`,
    data: { reviewedDate }
  });

const resolveInsuranceConflicts = (patientId, pickedInsuranceRows, ignoredInsuranceRows) =>
  axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/insuranceRows/resolveConflicts`,
    data: { pickedInsuranceRows, ignoredInsuranceRows }
  });

const sendSupportTicketAPI = (subject, comment, patient = null) => {
  const data = {
    ticket: {
      subject,
      comment,
      application: "Financial Navigation",
      ...(patient ? { patient } : {})
    }
  };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}support/create-ticket`,
    data
  });
};

const getFinancialNavigationPotentialSavingPhis = (queryIds) => {
  const data = queryIds.map((id) => ["ids[]", id]);

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSavingPhis?${urlEncodedData}`
  });
};

const getNotificationDataAPI = (notificationID) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}programNotifications/getNotificationData/${notificationID}`
  });

const getDrugsNameAPI = (papId, drugName) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}drugs?papId=${papId}&name=${drugName}`
  });

const getPapDrugCostCalculation = (papId) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}drugs/pap-drug-cost-calculation?papId=${papId}`
  });

const putDrugCostCalculationAPI = (papId, data) =>
  axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}paps/${papId}/drugCostCalculation`,
    data
  });

const getPatientDiagnoses = (patientId, excludeComputed = false) =>
  axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/patientDiagnoses?excludeComputed=${excludeComputed}`
  });

const patchPatientDiagnoses = (patientId, icd, data) =>
  axiosWithAuthentication({
    method: "PATCH",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/diagnoses/${icd}`,
    data
  });

const deletePatientDiagnoses = (patientId, icd) =>
  axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/diagnoses/${icd}`
  });

const createDiagnosisEvent = (patientId, icd, data) => {
  return axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/diagnoses/${icd}`,
    data
  });
};

const ignoreDiagnosisPending = (patientId, icd) =>
  axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/diagnoses/${icd}/pending`
  });

const replaceDiagnosisWithPending = (patientId, icd) =>
  axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/diagnoses/${icd}/pending/accepted`
  });

const rejectHighPriorityAPI = (reason, patientId) => {
  const data = { reason, patients: [patientId] };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}opportunities/patients/exclude`,
    data
  });
};

const rejectSingleHighPriorityPapAPI = (reason, pap) => {
  const data = { rejectReason: reason, pap };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}financialNavigationPotentialSaving/singleOpportunityExclude`,
    data
  });
};

const getPolicieServiceAPI = (policyId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}policies/${policyId}/services`
  });
};

const getMedicarePolicyDrugCoverageAPI = (policyId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}policies/${policyId}/medicareDrugCoverages`
  });
};

const getPatientMedicationsForManagePatientDetailsAPI = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/getPatientMedicationsForManagePatientDetails`
  });
};

const fillPdfApi = async ({
  papId,
  formApplicationShipping,
  applicationId,
  savePdf = false,
  applicationFormId = null,
  fileAttachmentName = ""
}) => {
  const data = {
    papId,
    applicationId,
    options: formApplicationShipping,
    applicationFormId
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}autofillForms/fill?savePdf=${savePdf}${
      fileAttachmentName ? `&saveAsFileName=${encodeURIComponent(fileAttachmentName)}` : ""
    }`,
    data,
    responseType: "blob"
  });
};

const starFilterList = (filterId, starred) => {
  const data = { filterId, starred };
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/star-filter-list`,
    data: data
  });
};

const deleteSavedListAPI = (listId) => {
  return axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}filterlists/delete/` + listId
  });
};

const automatePatientAfterUpdate = async ({
  patientId,
  treatmentMedicationUpdate = false,
  diagnosisUpdate = false,
  insuranceRowsUpdate = false,
  automateRule = MANUAL_AUTOMATION_SOURCES.MANUAL_REGULAR_AUTOMATION
}) => {
  const specificActions = [];

  if (diagnosisUpdate) {
    specificActions.unshift("diagnosisAutomation");
  }
  if (treatmentMedicationUpdate) {
    specificActions.unshift("patientActiveTreatmentAutomation");
  }
  if (insuranceRowsUpdate) {
    specificActions.push(
      "benefitInvestigationAutomation",
      "patientInsuranceRowsAutomation",
      "cobCalculationCacheAutomation"
    );
  }

  specificActions.push("potentialSavingsAutomation", "calculationAutomation", "potentialSavingUpdateAutomation");

  await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/automate/${patientId}`,
    data: {
      specificActions,
      automateRule
    }
  });

  if (treatmentMedicationUpdate) {
    await runPatientPrioritizationAPI(patientId);
  }
};

const runPatientPrioritizationAPI = (patientId) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/run-patient-prioritization`
  });
};

const submitExpressEnrollFormAPI = ({
  applicationId,
  attachmentIds,
  createPharmacyPatient,
  isCompleteForm = false
}) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationform/submit`,
    data: {
      applicationId,
      attachmentIds,
      createPharmacyPatient,
      isCompleteForm
    }
  }).catch((error) => {
    const responseError = error.response?.data?.errors?.[0].message;
    error.message = responseError ?? error.message;

    throw error;
  });
};

const pfizerApprovalApplicationMockAPI = (applicationId, papId) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}assistanceprogramapplications/aprovalmock/pfizer/${applicationId}`,
    data: { papId }
  });
};

const EEApproveApplicationMockAPI = (applicationFormId) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}ExpressEnrollmentMock/${applicationFormId}`
  });
};

const addAssistanceProgramAPI = ({ patientId, selectedAssistanceProgram, selectedDrug }) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/createnewpap`,
    data: { patientId, selectedAssistanceProgram, selectedDrug }
  });
};

const searchAssistanceProgramAPI = (searchQuery, filters) => {
  // filtersData code copied from sagaActions.searchAssistanceProgram
  let filtersData = {};
  Object.entries(filters).map(([filterName, values]) => {
    Object.entries(values).map(([value, text], index) => {
      if (typeof text !== "object") {
        filtersData = { ...filtersData, [`filters[${filterName}][${index}][value]`]: value };
        filtersData = { ...filtersData, [`filters[${filterName}][${index}][text]`]: text };
      } else {
        const newValues = text;
        Object.entries(newValues).map(([key, newValue]) => {
          filtersData = { ...filtersData, [`filters[${filterName}][${index}][${key}]`]: newValue };
        });
      }
    });
  });

  const formData = new FormData();

  formData.append("searchQuery", searchQuery);
  formData.append("sortAlphabetically", true);

  Object.entries(filtersData).map(([filter, value]) => formData.append(filter, value));

  const data = [...formData.entries()];

  const urlEncodedData = data
    .map((entry) => `${encodeURIComponent(entry[0])}=${encodeURIComponent(entry[1])}`)
    .join("&");

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}assistanceProgram/apFrontendSearch`,
    data: urlEncodedData
  });
};

const getApplicationByIdApi = (id) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}assistanceprogramapplications/${encodeURIComponent(id)}`
  });
};

const getApplicationFormByApplicationIdApi = (applicationId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}application/${encodeURIComponent(applicationId)}/applicationForm`
  });
};

const getAssistanceProgramApplicationItemsByAppId = (applicationId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}application/${applicationId}/assistanceProgramApplicationItem`
  });
};

const createApplicationFormApi = (applicationForm, isAutoFill = false) => {
  const data = {
    applicationForm: { ...applicationForm }
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/?isAutoFill=${isAutoFill}`,
    data
  });
};

const updateApplicationFormApi = (applicationForm) => {
  const data = {
    applicationForm: { ...applicationForm }
  };
  return axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/${applicationForm.id}`,
    data
  });
};

const generateApplicationFormPDFApi = (applicationFormId) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/${applicationFormId}/generatePdf`
  });
};

const generateDraftApplicationFormPDFApi = (applicationFormId) => {
  const data = {
    isDraft: true
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/${applicationFormId}/generatePdf`,
    data
  });
};

const unSignPdfApi = async (applicationFormId, signerType) => {
  const data = {
    signerType
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/${applicationFormId}/unSignPdf`,
    data,
    responseType: "blob"
  });
};

const deleteApplicationFormPDFApi = (applicationFormId, applicationFormAttachmentId) => {
  return axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/${applicationFormId}/applicationFormAttachment/${applicationFormAttachmentId}`
  });
};

const docusignSignatureRequestAPI = (applicationFormId, data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/${applicationFormId}/docusignSignatureRequest`,
    data
  });
};

const expressEnrollmentConfigurationByPapAndApplicationApi = (papId, applicationId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/expressEnrollmentConfigurationByPapAndApplication?papId=${papId}&applicationId=${applicationId}`
  });
};

const getRequiredSignersForAP = (apId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${REQUIRED_SIGNER}?apId=${apId}`
  });
};

const getApplicationFormSignatures = (applicationFormId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}applicationForm/${applicationFormId}/signatures`
  });
};

const getEConsentRequestAPI = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patient/${patientId}/remoteSignatureRequests/eConsent`
  });
};

// Consent Management APIs
const getLatestConsentEventAPI = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/${CONSENT_EVENTS}/latest`
  });
};

const getConsentHistoryAPI = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/${CONSENT_EVENTS}`
  });
};

const registerConsentAPI = (patientId, data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/${CONSENT_EVENTS}/accepted`,
    data
  });
};

const revokeConsentAPI = (patientId, data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/${CONSENT_EVENTS}/revoked`,
    data
  });
};

const createApplicationFormRemoteSignatureRequest = (data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${REMOTE_SIGNATURE_REQUESTS}/applicationForm`,
    data
  });
};

const createEConsentRemoteSignatureRequest = (data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}patient/${data.patientId}/${REMOTE_SIGNATURE_REQUESTS}/eConsent${
      data.collectFplDatum ? "?collectFplDatum=true" : ""
    }`,
    data
  });
};

const createFillNowSignature = (data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${FILL_NOW}`,
    data
  });
};

const revokeRemoteSignatureRequest = (signatureId) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${REMOTE_SIGNATURE_REQUESTS}/revoke/${signatureId}`
  });
};

// NOTE:: This API to send reminder might need to be changed
const sendSignatureReminder = ({
  applicationForm,
  signerType,
  email,
  phone,
  assignee,
  patientId,
  linkId,
  signatureRequestId,
  linkType
}) => {
  const data = {
    applicationForm,
    signerType,
    email,
    phone,
    assignee,
    patientId,
    linkId,
    linkType
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${REMOTE_SIGNATURE_REQUESTS}/reminder/${signatureRequestId}`,
    data
  });
};

const uploadApplicationFormAttachmentApi = (file, patientId, applicationId, fileAttachmentName) => {
  const data = new FormData();
  data.append("patientId", patientId);
  data.append("applicationId", applicationId);
  data.append("file", file, fileAttachmentName);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationformattachments/uploadManually`,
    data
  });
};

const uploadAPFAttachmentForEsignApi = (file, patientId, applicationId, fileAttachmentName) => {
  const data = new FormData();
  data.append("patientId", patientId);
  data.append("applicationId", applicationId);
  data.append("file", file, fileAttachmentName);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationformattachments/uploadForESign${
      fileAttachmentName ? `?saveAsFileName=${encodeURIComponent(fileAttachmentName)}` : ""
    }`,
    data
  });
};

const uploadWetSignaturePDFApi = (file, requiredSignerType, patientId, applicationId, fileAttachmentName) => {
  const data = new FormData();
  data.append("patientId", patientId);
  data.append("applicationId", applicationId);
  data.append("signerType", requiredSignerType);
  data.append("file", file, fileAttachmentName);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}wetsign`,
    data
  });
};

const signPdfManuallyApi = ({ file, patientId, applicationFormId, signerType }) => {
  const data = new FormData();
  data.append("patientId", patientId);
  data.append("signerType", signerType);
  data.append("file", file);

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationform/${applicationFormId}/signPdfManually`,
    data
  });
};

const cancelSignatureApi = async (applicationFormId, signerType) => {
  const data = {
    signerType: signerType
  };

  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}applicationform/${applicationFormId}/cancelSignature`,
    data
  });
};

const getSavedSignatureBySignerIdAPI = (signerType, signerId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${SAVED_SIGNATURES}/${signerType}/${signerId}`
  });
};

const createSavedSignatureAPI = (data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${SAVED_SIGNATURES}`,
    data
  });
};

const api = (methodName) => {
  const action = ApiMap[methodName];
  return (...params) =>
    new Promise((resolve, reject) => {
      const req = action(...params);
      axios(req)
        .then((res) => resolve(res.data))
        .catch((error) => reject(Object.assign(error, { methodName })));
    });
};

const postPortalDataThroughChromeExtension = (data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}accelerated-enrollment-bot/reportPortalData`,
    data
  });
};

const getRandomSuggestedQuestionsAIChatBot = (limit) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}ai-chat-bot/suggested-questions?limit=${limit}`
  });
};

const createAIChatBotConversation = () => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}ai-chat-bot/conversations`
  });
};

const getUserRecentAIChatBotConversation = (userId, limit) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}ai-chat-bot/users/${userId}/conversations?limit=${limit}`
  });
};

const getAIChatBotConversationHistory = (userId, conversationId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}ai-chat-bot/users/${userId}/conversations/${conversationId}/messages`
  });
};

const postFeedbackToMessage = (conversationId, messageId, feedback) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}ai-chat-bot/conversations/${conversationId}/messages/${messageId}/feedback`,
    data: { feedback }
  });
};

const postBadFeedbackResponse = (conversationId, messageId, badFeedbackResponse) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}ai-chat-bot/conversations/${conversationId}/messages/${messageId}/bad-feedback-response`,
    data: { badFeedbackResponse }
  });
};

addAuth(ApiNames.UpdateEnrollmentNote, (token, noteId, data) => {
  return {
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}${ENROLLMENT_NOTE}/${noteId}`,
    headers: authHeader(token),
    data
  };
});

addAuth(ApiNames.GetPatientBnPinDrugs, (token, patientId) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}patients/${patientId}/bn-pin-drugs`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.GetRxnormBnPinDrugs, (token, rxnormDrugIds) => {
  return {
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}rxnorm-drugs/bn-pin-drugs?rxnormDrugIds=${rxnormDrugIds}`,
    headers: authHeader(token)
  };
});

addAuth(ApiNames.PaytientSendToPatient, (token, data) => ({
  method: "POST",
  url: `${SERVER_URL}${API_VERSION_1}paytient/send-to-patient`,
  headers: authHeader(token),
  data
}));

addAuth(ApiNames.PaytientAnalytics, (token, filters) => ({
  method: "GET",
  url: `${SERVER_URL}${API_VERSION_1}paytient/analytics`,
  headers: authHeader(token),
  params: filters
}));

addAuth(ApiNames.PaytientWebLinkClick, (token, data) => ({
  method: "POST",
  url: `${SERVER_URL}${API_VERSION_1}paytient/web-link-click`,
  headers: authHeader(token),
  data
}));

const sendMessageToAIChatBot = async (conversationId, message) => {
  const token = getAccessToken();
  const url = `${SERVER_URL}${API_VERSION_1}ai-chat-bot/conversations/${conversationId}/messages`;
  const response = await fetch(url, {
    method: "POST",
    headers: authHeader(token),
    body: JSON.stringify({ ...message })
  });

  return response;
};

const getNotificationPreferences = async () => {
  return await axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}notification-preferences`
  });
};

// ============================================================================
// Portal Credentials API
// ============================================================================

const getPortalCredentials = async () => {
  return await axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}portal-credentials`
  });
};

const createOrUpdatePortalCredential = async (portalId, username, password) => {
  return await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}portal-credentials`,
    data: { portalId, username, password }
  });
};

const deletePortalCredential = async (credentialId) => {
  return await axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}portal-credentials/${credentialId}`
  });
};

const updateNotificationPreference = async (preferenceType, targetId, notificationType, isEnabled) => {
  return await axiosWithAuthentication({
    method: "PUT",
    url: `${SERVER_URL}${API_VERSION_1}notification-preferences`,
    data: {
      preferenceType,
      targetId,
      notificationType,
      isEnabled
    }
  });
};

// Paytient API functions
const sendToPatientAPI = (data) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}paytient/send-to-patient`,
    data
  });
};

const getPaytientAnalyticsAPI = (filters = {}) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}paytient/analytics`,
    params: filters
  });
};

const getPaytientEligiblePatientsAPI = () => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}paytient/eligible-patients`
  });
};

const getOverdueTasksCountAPI = () => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}todos/overdue-count`
  });
};

const getPendingTasksCountsAPI = () => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}todos/pending-counts`
  });
};

const bulkSendPaytientAPI = (patientIds, source = "bulk_send") => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}paytient/bulk-send`,
    data: { patientIds, source }
  });
};

const getExpressEnrollInvitesEligiblePatientsAPI = () => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}express-enroll-invites/eligible-patients`
  });
};

const bulkSendExpressEnrollInvitesAPI = (patientIds, source = "bulk_send") => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}express-enroll-invites/bulk-send`,
    data: { patientIds, source }
  });
};

// Automatic Approval API functions
const createAutomaticApprovalAPI = (actionType, metadata = {}) => {
  return axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}automatic-approval`,
    data: { actionType, metadata }
  });
};

const revokeAutomaticApprovalAPI = (approvalId) => {
  return axiosWithAuthentication({
    method: "DELETE",
    url: `${SERVER_URL}${API_VERSION_1}automatic-approval/${approvalId}`
  });
};

const getAllAutomaticApprovalsAPI = () => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}automatic-approval`
  });
};

const getActiveAutomaticApprovalsAPI = () => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}automatic-approval/active`
  });
};

const getAutomaticApprovalByTypeAPI = (actionType) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}automatic-approval/by-type/${actionType}`
  });
};

const checkShouldPromptForApprovalAPI = (actionType) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}automatic-approval/should-prompt/${actionType}`
  });
};

const getPatientOptOutStatusAPI = (patientId) => {
  return axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}paytient/opt-out-status/${patientId}`
  });
};

// Self-enrollment API functions
const getSelfEnrollMessagePreviewAPI = async ({ patientId, invitationType, programId = null, papId = null }) => {
  const params = {
    patientId,
    invitationType
  };

  if (programId != null && programId !== undefined && programId !== "") {
    params.programId = programId;
  }

  if (!_.isNil(papId) && papId !== "") {
    params.papId = papId;
  }

  const response = await axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${SELF_ENROLL}/get-message-preview`,
    params
  });

  return response.data;
};

const sendSelfEnrollInvitationAPI = async ({
  patientId,
  invitationType,
  programId,
  papId,
  deliveryMethod,
  includeRediDrugName
}) => {
  const payload = {
    patientId,
    invitationType,
    deliveryMethod
  };

  if (programId) {
    payload.programId = programId;
  }

  if (papId) {
    payload.papId = papId;
  }

  if (includeRediDrugName) {
    payload.includeRediDrugName = true;
  }

  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${SELF_ENROLL}/send-invitation`,
    data: payload
  });

  return response.data;
};

const getSelfEnrollInvitationHistoryAPI = async (filters = {}) => {
  const params = {};

  if (filters.page !== undefined) {
    params.page = filters.page;
  }

  if (filters.limit !== undefined) {
    params.limit = filters.limit;
  }

  const response = await axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${SELF_ENROLL}/invitation`,
    params
  });

  return response.data;
};

const getLatestInvitationByPatientAPI = async (patientId) => {
  const response = await axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${SELF_ENROLL}/invitation/patient/${patientId}`
  });

  return response.data;
};

const getPatientDetailsAPI = async (patientId) => {
  const response = await axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}${patientId}`
  });

  return response.data;
};

/**
 * Execute bulk action on multiple records
 * @param {Object} params - Bulk action parameters
 * @param {string} params.recordType - Type of records selected (patient, enrollment, claim)
 * @param {string} params.actionType - Action to perform (status, facility, physician, assignee_set, assignee_add, assignee_remove)
 * @param {number[]} params.recordIds - Array of record IDs to update (max 1000)
 * @param {any} params.value - New value to apply
 * @param {string} [params.targetEntity] - For cross-entity updates, the entity type to update (e.g., 'patient' when recordType is 'enrollment')
 * @returns {Promise<{totalRecords: number, succeededCount: number, failedCount: number, results: Array<{recordId: number, success: boolean, error?: string}>}>}
 */
const executeBulkActionAPI = async (params) => {
  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}bulk-actions`,
    data: params
  });

  return response.data;
};

/**
 * Get only record IDs for a list view (for "Select All" functionality)
 * Uses same API as dashboard search but with idsOnly=true query param
 * @param {string} selectedView - View identifier (e.g., 'all-patients', 'all-applications')
 * @param {Object} filters - Current filters applied to the view
 * @param {number} limit - Maximum number of IDs to return (default 1000)
 * @returns {Promise<{ids: number[]}>}
 */
const getRecordIdsOnlyAPI = async (selectedView, filters = {}, limit = 1000) => {
  // Transform filters using the same parsing logic as the regular search API
  // This flattens nested structures (dates, multiples, numbers) into the format the backend expects
  const parsedData = parseSearchFormParams({ selectedView, page: 1, limit, filters });

  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/phi-search?idsOnly=true`,
    data: {
      selectedView: parsedData.selectedView,
      filters: parsedData.filters,
      limit
    }
  });

  return response.data || { ids: [] };
};

/**
 * Fetch patient records by IDs (for refreshing data after bulk actions)
 * Uses the entity ID filter to fetch fully-enriched records
 * @param {string} selectedView - View identifier (e.g., 'all-patients')
 * @param {number[]} patientIds - Array of patient IDs to fetch
 * @returns {Promise<{patients: Object[], totalPatients: number}>}
 */
const getPatientsByIdsAPI = async (selectedView, patientIds) => {
  if (!patientIds || patientIds.length === 0) {
    return { patients: [], totalPatients: 0 };
  }

  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/phi-search`,
    data: {
      selectedView,
      filters: { patientIds },
      limit: patientIds.length
    }
  });

  return response.data || { patients: [], totalPatients: 0 };
};

/**
 * Fetch enrollment records by application IDs (for refreshing data after bulk actions).
 * Uses the enrollmentIds filter supported by allApplicationsView.
 * @param {string} selectedView - View identifier (e.g., 'all-applications')
 * @param {number[]} enrollmentIds - Array of application IDs to fetch
 * @returns {Promise<{patients: Object[], totalPatients: number}>}
 */
const getEnrollmentsByIdsAPI = async (selectedView, enrollmentIds) => {
  if (!enrollmentIds || enrollmentIds.length === 0) {
    return { patients: [], totalPatients: 0 };
  }

  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/phi-search`,
    data: {
      selectedView,
      filters: { enrollmentIds },
      limit: enrollmentIds.length
    }
  });

  return response.data || { patients: [], totalPatients: 0 };
};

/**
 * Fetch claim records by claim IDs (for refreshing data after bulk actions).
 * Uses the claimIds filter supported by allClaimsView.
 * @param {string} selectedView - View identifier (e.g., 'all-claims')
 * @param {number[]} claimIds - Array of claim IDs to fetch
 * @returns {Promise<{patients: Object[], totalPatients: number}>}
 */
const getClaimsByIdsAPI = async (selectedView, claimIds) => {
  if (!claimIds || claimIds.length === 0) {
    return { patients: [], totalPatients: 0 };
  }

  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${PATIENTS}dashboard/phi-search`,
    data: {
      selectedView,
      filters: { claimIds },
      limit: claimIds.length
    }
  });

  return response.data || { patients: [], totalPatients: 0 };
};

/**
 * Fetches active Express Enroll banners for a specific template and optionally drug.
 * Server-side filtering returns only enabled banners matching the given criteria.
 * @param {string} templateName - The template name (e.g., 'genentech-copay', 'pfizer-copay')
 * @param {string} [drugName] - Optional drug name to filter by (e.g., 'Perjeta', 'Tecentriq')
 * @returns {Promise<Array>} Array of active banner objects
 */
const getActiveExpressEnrollBannersAPI = async (templateName, drugName) => {
  const params = { templateName };
  if (drugName) {
    params.drugName = drugName;
  }
  const response = await axiosWithAuthentication({
    method: "GET",
    url: `${SERVER_URL}${API_VERSION_1}expressEnrollBanners/active`,
    params
  });
  return response.data;
};

/**
 * Checks if Redi invitation modal should be displayed and prepares invitation data
 * Smart endpoint that checks eligibility, enforces display limits, and returns ready-to-use link
 * @param {number} patientId - The patient ID
 * @param {number} journeyId - The journey ID
 * @param {string} action - Action that triggered the check (use REDI_ACTION_TYPES constants)
 * @returns {Promise<Object>} Response object with shouldShowModal flag and invitation data
 */
const checkRediInvitationApi = async (patientId, journeyId, action) => {
  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${REDI}/fn-action/check-and-prepare`,
    data: {
      patientId,
      journeyId,
      action
    }
  });
  return response.data;
};

const sendRediInvitationApi = async (prescriptionId, preferences) => {
  const response = await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${REDI}/fn-action/send-invitation`,
    data: {
      prescriptionId,
      preferences
    }
  });
  return response.data;
};

/**
 * Notify backend that the Redi invitation popup was actually displayed to the user.
 * Used so display limits (max 2, 24h cooldown) are only applied after confirmed display.
 * @param {number} prescriptionId - RediPrescription record ID
 */
const reportRediPopupDisplayedApi = async (prescriptionId) => {
  await axiosWithAuthentication({
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}${REDI}/fn-action/redi-popup-displayed`,
    data: { prescriptionId }
  });
};

addAuth(ApiNames.FrontendLog, (token, message) => {
  return {
    method: "POST",
    url: `${SERVER_URL}${API_VERSION_1}frontend-logs`,
    headers: authHeader(token),
    data: { message }
  };
});

export {
  addAssistanceProgramAPI,
  addDrugAPI,
  addNewPatient,
  addPatientNoteAPI,
  api,
  ApiBaseUrl,
  ApiCallerMap,
  ApiMap,
  ApiNames,
  automatePatientAfterUpdate,
  CallerType,
  cancelDocusignSignatureRequestAPI,
  cancelSignatureApi,
  checkOtherPayers,
  CLAIM_ATTACHMENTS,
  PATIENTS,
  SELF_ENROLL,
  countApplicationAttachmentApi,
  createApplicationFormApi,
  createDiagnosisEvent,
  createFillNowSignature,
  createPatientInsurancesReviewAPI,
  createPatientsTaskAPI,
  createApplicationFormRemoteSignatureRequest,
  createEConsentRemoteSignatureRequest,
  deleteApplicationAttachmentApi,
  deleteApplicationFormPDFApi,
  deleteMedicationAPI,
  deletePatientAttachmentAPI,
  deletePatientDiagnoses,
  deletePatientNoteAPI,
  deletePatientsTaskAPI,
  deleteSavedListAPI,
  docusignSignatureRequestAPI,
  downloadApplicationAttachmentApi,
  downloadApplicationFormAttachmentApi,
  downloadClaimAttachmentAPI,
  downloadPatientAttachmentAPI,
  editMedicationAPI,
  editPatientNoteAPI,
  editPatientsTaskAPI,
  EEApproveApplicationMockAPI,
  expressEnrollmentConfigurationByPapAndApplicationApi,
  fillPdfApi,
  generateApplicationFormPDFApi,
  generateDraftApplicationFormPDFApi,
  getApplicationFormByApplicationIdApi,
  getApplicationByIdApi,
  getApplicationFormSignatures,
  getApplicationUsedAmountsAPI,
  getAssistanceProgramApplicationItemsByAppId,
  getClaimAttachmentAPI,
  getClaimNotesAPI,
  getDrugsNameAPI,
  getFinancialNavigationPotentialSavingPhis,
  getImportedStatusesAPI,
  getLoginImagesAPI,
  getManagePatientDetails,
  getMedicarePolicyDrugCoverageAPI,
  getNotificationDataAPI,
  getNotificationsAPI,
  getNumberOfPatientsAPI,
  getSelfEnrollInvitationsAPI,
  getInvitationsByPatientIdAPI,
  getPapDrugCostCalculation,
  getPatientAttachmentsAPI,
  getPatientAttachmentsCountAPI,
  getPatientBnPinDrugsAPI,
  getPatientData,
  getPatientDiagnoses,
  getPatientMedicationsForManagePatientDetailsAPI,
  getPatientNotesAPI,
  getPatientsTasksAPI,
  getPharmacyPatientsOrdersAPI,
  getPolicieServiceAPI,
  getRequiredSignersForAP,
  getTopOpportunityPatientsAPI,
  getValidForEbvPayersAPI,
  ignoreDiagnosisPending,
  initializePendoAnonymous,
  listApplicationAttachmentApi,
  markNotificationAPI,
  parseSearchFormParams,
  patchPatientDiagnoses,
  pfizerApprovalApplicationMockAPI,
  postPortalDataThroughChromeExtension,
  putDrugCostCalculationAPI,
  rejectHighPriorityAPI,
  rejectSingleHighPriorityPapAPI,
  replaceDiagnosisWithPending,
  requestManualAnalysis,
  resolveInsuranceConflicts,
  revokeRemoteSignatureRequest,
  runPatientPrioritizationAPI,
  searchAssistanceProgramAPI,
  searchDiagnosisIcdsAPI,
  searchDiagnosisNamesAPI,
  searchIcdToDiagnoses,
  searchPatientPayers,
  searchRxnormDrugsAPI,
  sendSignatureReminder,
  sendSupportTicketAPI,
  SERVER_URL,
  serverFiltersDecoder,
  setPatientDetailsAPI,
  signPdfManuallyApi,
  starFilterList,
  starPatient,
  submitExpressEnrollFormAPI,
  unSignPdfApi,
  updateApplicationFormApi,
  updateApplicationUsedAmountsAPI,
  updateFollowupDateAPI,
  updateInsuranceAPI,
  updatePatientAssigneesAPI,
  updatePatientFacility,
  updatePatientMedicationsAPI,
  updatePatientPhysician,
  updatePatientStatusAPI,
  updatePreferredLanguageAPI,
  updateUninsuredStatusAPI,
  uploadAPFAttachmentForEsignApi,
  uploadApplicationAttachmentApi,
  uploadApplicationFormAttachmentApi,
  uploadAttachmentAPI,
  uploadPatientAttachmentAPI,
  uploadWetSignaturePDFApi,
  validateMrnUniqueness,
  viewApplicationAttachmentApi,
  viewPatientAttachmentURL,
  getEConsentRequestAPI,
  getLatestConsentEventAPI,
  getConsentHistoryAPI,
  registerConsentAPI,
  revokeConsentAPI,
  exportTasksReportAPI,
  exportListAPI,
  createSavedSignatureAPI,
  getSavedSignatureBySignerIdAPI,
  createAIChatBotConversation,
  getUserRecentAIChatBotConversation,
  getAIChatBotConversationHistory,
  getRandomSuggestedQuestionsAIChatBot,
  sendMessageToAIChatBot,
  postFeedbackToMessage,
  postBadFeedbackResponse,
  getNotificationPreferences,
  updateNotificationPreference,
  sendToPatientAPI,
  getPaytientAnalyticsAPI,
  getPaytientEligiblePatientsAPI,
  getOverdueTasksCountAPI,
  getPendingTasksCountsAPI,
  bulkSendPaytientAPI,
  createAutomaticApprovalAPI,
  revokeAutomaticApprovalAPI,
  getAllAutomaticApprovalsAPI,
  getActiveAutomaticApprovalsAPI,
  getAutomaticApprovalByTypeAPI,
  checkShouldPromptForApprovalAPI,
  getPatientOptOutStatusAPI,
  getSelfEnrollMessagePreviewAPI,
  sendSelfEnrollInvitationAPI,
  getSelfEnrollInvitationHistoryAPI,
  getLatestInvitationByPatientAPI,
  getPatientDetailsAPI,
  getPortalCredentials,
  createOrUpdatePortalCredential,
  deletePortalCredential,
  executeBulkActionAPI,
  getRecordIdsOnlyAPI,
  getPatientsByIdsAPI,
  getEnrollmentsByIdsAPI,
  getClaimsByIdsAPI,
  getActiveExpressEnrollBannersAPI,
  checkRediInvitationApi,
  sendRediInvitationApi,
  reportRediPopupDisplayedApi,
  getExpressEnrollInvitesEligiblePatientsAPI,
  bulkSendExpressEnrollInvitesAPI
};
